import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Crown } from "lucide-react";

const TIER_STYLES = {
  Bronze:   { grad: "from-[#7A4A22] via-[#B87333] to-[#7A4A22]", text: "#F5DFC8" },
  Silver:   { grad: "from-[#5C5C5C] via-[#C0C0C0] to-[#5C5C5C]", text: "#0F0F0F" },
  Gold:     { grad: "from-[#8B6010] via-[#E8B84B] to-[#8B6010]", text: "#0F0F0F" },
  Platinum: { grad: "from-[#8A8A8A] via-[#E5E4E2] to-[#8A8A8A]", text: "#0F0F0F" },
};

export default function ShreedhaniRankCard() {
  const [rank, setRank] = useState(null);

  useEffect(() => {
    api.get("/user/rank").then(({ data }) => setRank(data));
  }, []);

  if (!rank) return null;
  const style = TIER_STYLES[rank.name] || TIER_STYLES.Bronze;

  return (
    <div data-testid="shreedhani-rank-card" className="rounded-3xl border border-brand-line bg-brand-sand p-5 shadow-soft overflow-hidden relative">
      <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-brand-primary/8 blur-3xl" />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-bold tracking-[0.22em] uppercase text-brand-primary">Your Rank</div>
          <div className={`mt-2 font-display text-4xl font-semibold bg-gradient-to-r ${style.grad} bg-clip-text text-transparent`}>
            {rank.title}
          </div>
          <div className="mt-1 text-xs text-brand-slate tabular-nums">
            {rank.saved_grams_ytd.toFixed(3)}g saved this year (gold equivalent)
          </div>
        </div>
        <div className={`h-14 w-14 rounded-full bg-gradient-to-br ${style.grad} flex items-center justify-center shadow-gold`}>
          <Crown size={22} color={style.text} strokeWidth={2.4} />
        </div>
      </div>

      {rank.next_tier ? (
        <>
          <div className="mt-5">
            <div className="flex items-end justify-between text-[11px] text-brand-slate">
              <span>{rank.name}</span>
              <span className="tabular-nums">
                {rank.grams_to_next.toFixed(3)}g to <span className="text-brand-cream font-semibold">{rank.next_tier}</span>
              </span>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-brand-sand2 overflow-hidden">
              <div
                data-testid="rank-progress-bar"
                className="h-full bg-gold-gradient transition-all duration-700"
                style={{ width: `${Math.max(2, Math.min(100, rank.progress_pct))}%` }}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="mt-4 text-xs text-brand-accent">You've reached the highest rank. ✦ Namaskar, Shreedhan-Ratna.</div>
      )}

      <div data-testid="rank-tier-strip" className="mt-4 grid grid-cols-4 gap-2">
        {rank.tiers.map((t) => {
          const active = t.name === rank.name;
          const s = TIER_STYLES[t.name];
          return (
            <div
              key={t.name}
              className={`rounded-2xl px-2 py-2 text-center border ${active ? "border-brand-primary bg-brand-sand2" : "border-brand-line-faint bg-transparent"}`}
            >
              <div
                className={`mx-auto h-6 w-6 rounded-full bg-gradient-to-br ${s.grad} ${active ? "shadow-gold" : "opacity-60"}`}
              />
              <div className={`mt-1 text-[10px] font-semibold ${active ? "text-brand-cream" : "text-brand-slate2"}`}>{t.name}</div>
              <div className="text-[9px] text-brand-slate2 tabular-nums">
                {t.next ? `${t.min_grams}–${t.next}g` : `${t.min_grams}g+`}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
